import { Box, Col } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';

/**
 * Maps the landing-page theme's semantic background tokens to Atoms background
 * utilities. The shared landing-page theme (`getBNComponentThemeColors`) still
 * speaks in these token strings, so we translate them at the container boundary.
 */
const BACKGROUND_COLOR_CLASS = {
  $backgroundPrimary: 'bg-od-bg-primary',
  $backgroundSecondary: 'bg-od-bg-secondary',
  $backgroundTertiary: 'bg-od-bg-tertiary',
  $backgroundInversePrimary: 'bg-od-bg-inverse-primary',
} as const;

/** Background tokens this container can render. */
export type ContainerBackgroundColor = keyof typeof BACKGROUND_COLOR_CLASS;

const CONTENT_CLASS = 'block w-full max-w-360 px-5 md:px-10 lg:px-16';

interface ContainerProps {
  children: React.ReactNode;
  /** Semantic background token applied to the full-bleed wrapper. */
  backgroundColor?: ContainerBackgroundColor;
  /** Classes applied to the inner max-width content box. */
  className?: string;
  id?: string;
  noSectionSpacing?: boolean;
}

const Container = ({
  children,
  backgroundColor,
  className,
  id,
  noSectionSpacing,
}: ContainerProps) => {
  return (
    <Box className={cn('block', !noSectionSpacing && 'lp-section')}>
      <Col
        className={cn('items-center', backgroundColor && BACKGROUND_COLOR_CLASS[backgroundColor])}
      >
        <Box id={id} className={cn(CONTENT_CLASS, className)}>
          {children}
        </Box>
      </Col>
    </Box>
  );
};

const NetProceedsCalculatorContainer = ({ children, className, id }: ContainerProps) => {
  return (
    <Box className="w-full justify-center">
      <Box id={id} className={cn(CONTENT_CLASS, className)}>
        {children}
      </Box>
    </Box>
  );
};

const LandingPageSectionContainer = ({ children, className, id }: ContainerProps) => {
  return (
    <Col className="lp-section w-full items-center">
      <Box id={id} className={cn(CONTENT_CLASS, 'mt-8', className)}>
        {children}
      </Box>
    </Col>
  );
};

export { LandingPageSectionContainer, NetProceedsCalculatorContainer };

export default Container;
